import React, { useMemo, useState } from 'react';
import { BadgeCheckIcon, FlagIcon, InfoIcon, LockIcon, ShieldAlertIcon, SlidersHorizontalIcon, UndoIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { ChoiceGroup } from '../../components/ui/ChoiceGroup';
import { EmptyState } from '../../components/ui/EmptyState';
import { Field, Input, Select } from '../../components/ui/Field';
import { PermissionGate, GrantedVia } from '../../components/admin/PermissionGate';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { useGovernance } from '../../contexts/GovernanceContext';
import { achievementDefinitions, recommendationSignals, socialApprovals, socialCases, socialMetrics, socialTaxonomy, type ApprovalItemState, type SocialApprovalItem, type SocialCase, type SocialCaseState } from '../../data/socialAdmin';
import type { Scope } from '../../types/governance';
const TABS = ['Approvals', 'Moderation', 'Taxonomy', 'Achievements', 'Recommendations', 'Analytics'] as const;
const CASE_STATE: Record<SocialCaseState, {
  label: string;
  tone: 'warn' | 'brand' | 'ok' | 'neutral' | 'danger';
}> = {
  open: {
    label: 'Open',
    tone: 'warn'
  },
  under_review: {
    label: 'Under review',
    tone: 'brand'
  },
  actioned: {
    label: 'Actioned',
    tone: 'ok'
  },
  dismissed: {
    label: 'Dismissed',
    tone: 'neutral'
  },
  appealed: {
    label: 'Appealed',
    tone: 'danger'
  }
};
const APPROVAL_STATE: Record<ApprovalItemState, {
  label: string;
  tone: 'warn' | 'ok' | 'neutral' | 'brand';
}> = {
  pending: {
    label: 'Waiting on review',
    tone: 'warn'
  },
  approved: {
    label: 'Approved',
    tone: 'ok'
  },
  declined: {
    label: 'Declined',
    tone: 'neutral'
  },
  more_info: {
    label: 'Information requested',
    tone: 'brand'
  }
};

/** True when the case or record sits inside the scope the console is looking at. */
function inScope(active: Scope, itemScope: Scope): boolean {
  if (active.level === 'global') return true;
  if (active.level === 'tenant') return active.tenantId === itemScope.tenantId;
  if (active.level === 'institution') return active.institutionId === itemScope.institutionId;
  return active.campusId === itemScope.campusId;
}

/**
 * Social & Campus Life administration.
 *
 * Everything here reads the same governance engine as the rest of the console:
 * a campus-scoped moderator sees only their campus, and a refusal names the
 * permission it needed rather than hiding the section.
 */
export function SocialControl() {
  const {
    activeScope,
    scopeLabel,
    allows,
    logAction,
    recordAttempt
  } = useGovernance();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Approvals');
  const [approvals, setApprovals] = useState(socialApprovals);
  const [cases, setCases] = useState(socialCases);
  const [signals, setSignals] = useState(recommendationSignals);
  const [definitions, setDefinitions] = useState(achievementDefinitions);
  const visibleApprovals = useMemo(() => approvals.filter(item => inScope(activeScope, item.scope)), [activeScope, approvals]);
  const visibleCases = useMemo(() => cases.filter(item => inScope(activeScope, item.scope)), [activeScope, cases]);
  const outOfScopeCases = cases.length - visibleCases.length;
  return <div className="space-y-6" data-id="element-6913" data-mp="2lafrcrnbc0i">
      <PageHeader eyebrow="Social & campus life" title="Social control centre" description="Community and club approval, campus feed moderation, taxonomy, achievement definitions, recommendation signals and participation analytics — all scoped to the campuses you administer." actions={<ScopeSwitcher data-id="element-6915" data-mp="2eabdunbkmjcm" />} data-id="element-6914" data-mp="7jlo1j2alegm" />

      <Panel data-id="element-6916" data-mp="2e98q2o9jox0x">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-6917" data-mp="2e8gvngrop5c8">
          <Badge plain data-id="element-6918" data-mp="2ja65wu731ez9">Viewing: {scopeLabel(activeScope)}</Badge>
          <Badge tone="warn" data-id="element-6919" data-mp="2yirr4cg3hb3j">Development data — not production metrics</Badge>
          {outOfScopeCases > 0 && <span className="text-body-sm text-ink-muted" data-id="element-6920" data-mp="2trjhpgerlpqd">
              {outOfScopeCases} cases exist outside this scope and are not shown or counted.
            </span>}
        </div>
        <p className="mt-3 flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-6921" data-mp="2krmuzkbxr0xy">
          <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-6922" data-mp="3hhnmvs7lpxjh" />
          No role in this console grants access to private messages, diary entries, notes, wellbeing
          records or roommate preferences. Reported content is shown; the surrounding private
          conversation is not.
        </p>
      </Panel>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Social administration sections" data-id="element-6923" data-mp="33gtayzlcheg2" />

      {tab === 'Approvals' && <PermissionGate permission="moderation.resolve" label="Approving communities, clubs and organisations" data-id="element-6924" data-mp="1x33rdi3tzcc7">
          <div className="space-y-4" data-id="element-6925" data-mp="7l6uleyli416">
            <Panel title="How approval works" data-id="element-6926" data-mp="7m86flnfqujy">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-6927" data-mp="cjrm8pzs7fe0">
                Approving a community lets it operate. Verifying a club is a separate, stronger claim
                — it says the institution's own register was checked. A club can be approved and
                still not verified, and the student-facing page says so plainly rather than staying
                silent.
              </p>
            </Panel>

            {visibleApprovals.length === 0 ? <Panel data-id="element-6928" data-mp="vcc7pmd5ii7s">
                <EmptyState icon={<BadgeCheckIcon className="h-5 w-5" data-id="element-6930" data-mp="10adtwg5vixnh" />} title="Nothing waiting in this scope" description="Widen the scope if you administer more than one campus." data-id="element-6929" data-mp="109cddw639iyd" />
              </Panel> : <ul className="space-y-4" data-id="element-6931" data-mp="362as1xpanp0e">
                {visibleApprovals.map(item => <li key={item.id} data-id="element-6932" data-mp="3643x58k9e16a">
                    <ApprovalCard item={item} onDecide={(state, reason) => {
              setApprovals(current => current.map(entry => entry.id === item.id ? {
                ...entry,
                state,
                decidedBy: '@you',
                decisionReason: reason
              } : entry));
              logAction({
                action: state === 'approved' ? `Approved ${item.kind}` : state === 'declined' ? `Declined ${item.kind}` : `Requested more information on ${item.kind}`,
                resource: `social.${item.kind}`,
                resourceId: item.id,
                before: item.state,
                after: state,
                reason
              });
            }} data-id="element-6933" data-mp="d70v5sm3sil1" />
                  </li>)}
              </ul>}
          </div>
        </PermissionGate>}

      {tab === 'Moderation' && <ModerationQueues cases={visibleCases} onDecide={(item, state, action, reason) => {
      const decision = allows('moderation.resolve', item.scope);
      if (!decision.allowed) {
        recordAttempt({
          action: 'Resolve social moderation case',
          resource: 'social.case',
          resourceId: item.id,
          reason: decision.message
        });
        return;
      }
      setCases(current => current.map(entry => entry.id === item.id ? {
        ...entry,
        state,
        action,
        actionReason: reason,
        assignedTo: '@you'
      } : entry));
      logAction({
        action: `Social case ${state}`,
        resource: 'social.case',
        resourceId: item.id,
        before: item.state,
        after: state,
        reason
      });
    }} data-id="element-6934" data-mp="1oki9ypvgsjs5" />}

      {tab === 'Taxonomy' && <TaxonomyPanel data-id="element-6935" data-mp="yasywzklk81k" />}

      {tab === 'Achievements' && <PermissionGate permission="flags.edit" label="Changing achievement definitions" data-id="element-6936" data-mp="tchmch2x3jfn">
          <div className="space-y-4" data-id="element-6937" data-mp="om18ivv6pvsr">
            <Panel title="Achievement definitions" data-id="element-6938" data-mp="okzwop6ch59z">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-6939" data-mp="ok7z58t6sxh2">
                An achievement is only offered when a record exists that proves it. There is
                deliberately no points total and no definition that rewards opening the app — the one
                below marked inactive is kept visible so nobody re-adds it by accident.
              </p>
            </Panel>
            <Panel bodyClassName="p-0" data-id="element-6940" data-mp="5p9t4xirk0tz">
              <ul className="divide-y divide-line" data-id="element-6941" data-mp="2hb1y9hrza9hh">
                {definitions.map(definition => <li key={definition.id} className="px-5 py-4 sm:px-6" data-id="element-6942" data-mp="2m8u0b4mc7bze">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6943" data-mp="2qb72jj1vkrrf">
                      <div className="min-w-0" data-id="element-6944" data-mp="2vaba6595ihfn">
                        <p className="text-body font-semibold text-ink" data-id="element-6945" data-mp="2v90bgjukbzve">{definition.label}</p>
                        <p className="mt-0.5 max-w-[72ch] text-body-sm text-ink" data-id="element-6946" data-mp="1ylc2osge5joi">
                          Earned from: {definition.earnedFrom}
                        </p>
                        <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6947" data-mp="27k7842bm2pqr">
                          Awarded by {definition.awardedBy} ·{' '}
                          <span className="cz-num" data-id="element-6948" data-mp="2ccqmh1i4howm">{definition.awarded}</span> held
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6949" data-mp="1jv4r8wosx0xb">
                        <Badge tone={definition.active ? 'ok' : 'neutral'} data-id="element-6950" data-mp="1jss29zkmlqar">
                          {definition.active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button variant="secondary" size="sm" onClick={() => {
                    setDefinitions(current => current.map(entry => entry.id === definition.id ? {
                      ...entry,
                      active: !entry.active
                    } : entry));
                    logAction({
                      action: definition.active ? 'Deactivated achievement definition' : 'Activated achievement definition',
                      resource: 'social.achievement',
                      resourceId: definition.id,
                      before: definition.active ? 'active' : 'inactive',
                      after: definition.active ? 'inactive' : 'active',
                      reason: 'Changed from the social control centre'
                    });
                  }} data-id="element-6951" data-mp="2c918osierx4m">
                          {definition.active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </div>
                    {!definition.active && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-6952" data-mp="fj6yjn7dsmrr">
                        Deactivating stops new awards. Achievements already held are preserved —
                        nothing is taken back from a student's record.
                      </p>}
                  </li>)}
              </ul>
            </Panel>
          </div>
        </PermissionGate>}

      {tab === 'Recommendations' && <PermissionGate permission="flags.edit" label="Changing recommendation signals" data-id="element-6953" data-mp="3i6g0k2zwy2a1">
          <div className="space-y-4" data-id="element-6954" data-mp="3n59gs36l47dw">
            <Panel title="Recommendation signals" data-id="element-6955" data-mp="3n6c3dywvjno0">
              <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-6956" data-mp="395huzg9ttmpc">
                Every signal used to suggest people, communities, clubs or events is listed here, and
                every one of them is shown to the student as the reason on the suggestion itself.
                Two signals are permanently off and cannot be enabled from this console: approximate
                location and roommate preferences.
              </p>
            </Panel>
            <Panel bodyClassName="p-0" data-id="element-6957" data-mp="3s3kreg4pelx6">
              <ul className="divide-y divide-line" data-id="element-6958" data-mp="3s2jbnycqtb94">
                {signals.map(signal => <li key={signal.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5 px-5 py-4 sm:px-6" data-id="element-6959" data-mp="vq329fme9uk">
                    <div className="min-w-0" data-id="element-6960" data-mp="tggj8namm878">
                      <p className="text-body font-semibold text-ink" data-id="element-6961" data-mp="12sdwfudb6n8j">{signal.label}</p>
                      <p className="mt-0.5 max-w-[72ch] text-body-sm text-ink" data-id="element-6962" data-mp="ys3ucssn0gyy">{signal.detail}</p>
                      {signal.studentControllable && <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6963" data-mp="1cl374n11bx2t">
                          A student can switch this off for their own account in Settings.
                        </p>}
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6964" data-mp="ft7xtatb3qhy">
                      {signal.studentControllable ? <Button variant={signal.enabled ? 'secondary' : 'primary'} size="sm" onClick={() => {
                  setSignals(current => current.map(entry => entry.id === signal.id ? {
                    ...entry,
                    enabled: !entry.enabled
                  } : entry));
                  logAction({
                    action: signal.enabled ? 'Disabled recommendation signal' : 'Enabled recommendation signal',
                    resource: 'social.signal',
                    resourceId: signal.id,
                    before: signal.enabled ? 'enabled' : 'disabled',
                    after: signal.enabled ? 'disabled' : 'enabled',
                    reason: 'Changed from the social control centre'
                  });
                }} data-id="element-6965" data-mp="tu2aw6raufmy">
                          {signal.enabled ? 'Disable' : 'Enable'}
                        </Button> : <Badge tone="neutral" icon={<LockIcon className="h-4 w-4" data-id="element-6967" data-mp="1gf4itadapssj" />} data-id="element-6966" data-mp="1gfmr55d8fxgt">
                          Permanently off
                        </Badge>}
                    </div>
                  </li>)}
              </ul>
            </Panel>
          </div>
        </PermissionGate>}

      {tab === 'Analytics' && <div className="space-y-4" data-id="element-6968" data-mp="2rxij5hz31e73">
          <Panel title="What we measure" data-id="element-6969" data-mp="2myffo1yr3g18">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-6970" data-mp="2mzqj20oaalrt">
              Time spent is deliberately not measured. Every figure below is either participation,
              usefulness or safety, scoped to {scopeLabel(activeScope)} — and each names the record
              it is derived from, so a number cannot mean two things.
            </p>
          </Panel>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-6971" data-mp="35xjnj7ek33rb">
            {socialMetrics.map(metric => <li key={metric.id} data-id="element-6972" data-mp="782y73mjojpd">
                <Panel className="h-full" bodyClassName="flex h-full flex-col p-5" data-id="element-6973" data-mp="794b7dn4fg9q">
                  <span className="cz-num text-h3 font-semibold text-ink" data-id="element-6974" data-mp="2eqen7wq333h">{metric.value}</span>
                  <span className="mt-1 text-body font-medium text-ink" data-id="element-6975" data-mp="ld3bz2sf98gd">{metric.label}</span>
                  <span className="mt-1 text-body-sm text-ink-muted" data-id="element-6976" data-mp="qabl6dvle25h">{metric.basis}</span>
                  <span className="mt-auto pt-3" data-id="element-6977" data-mp="cj6nhhbsbjxs">
                    <Badge plain data-id="element-6978" data-mp="gltn44ep695j">{metric.measures}</Badge>
                  </span>
                </Panel>
              </li>)}
          </ul>
        </div>}
    </div>;
}
function ApprovalCard({
  item,
  onDecide
}: {
  item: SocialApprovalItem;
  onDecide: (state: ApprovalItemState, reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const state = APPROVAL_STATE[item.state];
  const decided = item.state !== 'pending';
  return <Panel title={item.name} description={`${item.kind} · ${item.category} · requested by ${item.requestedBy}`} action={<div className="flex flex-wrap items-center gap-2" data-id="element-6980" data-mp="1s0qcxgiph2dy">
          <Badge tone={state.tone} data-id="element-6981" data-mp="1s073ypb19oc2">{state.label}</Badge>
          <GrantedVia permission="moderation.resolve" scope={item.scope} data-id="element-6982" data-mp="3ci9dqzzm6r7b" />
        </div>} data-id="element-6979" data-mp="3tsn1bqpz26q5">
      <dl className="grid gap-4 lg:grid-cols-2" data-id="element-6983" data-mp="38i8oekaz8vlh">
        <div data-id="element-6984" data-mp="38fcqgvz4q6x1">
          <dt className="text-body-sm text-ink-muted" data-id="element-6985" data-mp="3m8u645op2zhs">Campus</dt>
          <dd className="text-body text-ink" data-id="element-6986" data-mp="3maf4y7i1l4lg">{item.campusLabel}</dd>
        </div>
        <div data-id="element-6987" data-mp="3hhc5jiql30xc">
          <dt className="text-body-sm text-ink-muted" data-id="element-6988" data-mp="3hgkodcxqh5ql">Purpose stated</dt>
          <dd className="max-w-[70ch] text-body text-ink" data-id="element-6989" data-mp="kurdbm8waw">{item.purpose}</dd>
        </div>
        <div data-id="element-6990" data-mp="3r8qcucos1511">
          <dt className="text-body-sm text-ink-muted" data-id="element-6991" data-mp="3r6vsp0lbxgy0">What the requester claims</dt>
          <dd className="max-w-[70ch] text-body text-ink" data-id="element-6992" data-mp="92kcy8yda6ff">{item.claim}</dd>
        </div>
        <div data-id="element-6993" data-mp="42pn88qbvmvy">
          <dt className="text-body-sm text-ink-muted" data-id="element-6994" data-mp="42zavpnwhhyh">What has actually been confirmed</dt>
          <dd className="max-w-[70ch] text-body text-ink" data-id="element-6995" data-mp="2asvsdoiknpa8">
            {item.confirmed.length === 0 ? <span className="flex items-start gap-2 text-ink-muted" data-id="element-6996" data-mp="25vdq2gz288b7">
                <ShieldAlertIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-700" data-id="element-6997" data-mp="1kz6842o2efky" />
                Nothing confirmed yet. A claim is not evidence.
              </span> : <ul className="space-y-1" data-id="element-6998" data-mp="1trhxdl7fzwzc">
                {item.confirmed.map(entry => <li key={entry} data-id="element-6999" data-mp="1yqbavdmqbnzi">{entry}</li>)}
              </ul>}
          </dd>
        </div>
      </dl>

      {decided ? <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-7000" data-mp="121mwcra82iuu">
          <p className="text-body-sm text-ink" data-id="element-7001" data-mp="160vs21otoqt1">
            <span className="text-ink-muted" data-id="element-7002" data-mp="163qbpr00i7le">Decision by {item.decidedBy}: </span>
            {item.decisionReason}
          </p>
        </div> : <div className="mt-4 border-t border-line pt-4" data-id="element-7003" data-mp="1g0uxvqtuy327">
          <Field label="Reason for your decision" htmlFor={`reason-${item.id}`} hint="Required. The requester is shown this, and it is written to the audit log." data-id="element-7004" data-mp="2mfvxc6i3d3dj">
            <Input id={`reason-${item.id}`} value={reason} onChange={event => setReason(event.target.value)} data-id="element-7005" data-mp="frdguzofz2ct" />
          </Field>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-7006" data-mp="auwizdrfx9mj">
            <Button variant="primary" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide('approved', reason.trim())} data-id="element-7007" data-mp="60ir3lbzlkmm">
              Approve
            </Button>
            <Button variant="secondary" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide('more_info', reason.trim())} data-id="element-7008" data-mp="3o7gcg72a50ks">
              Request more information
            </Button>
            <Button variant="danger" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide('declined', reason.trim())} data-id="element-7009" data-mp="3eb3mnk9uv6vb">
              Decline
            </Button>
          </div>
          {reason.trim().length === 0 && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7010" data-mp="25ul6w4ckebru">
              A decision cannot be recorded without a reason.
            </p>}
        </div>}
    </Panel>;
}
const QUEUES = ['All', 'Posts', 'Comments', 'Communities', 'Clubs', 'People', 'Stories'] as const;
function ModerationQueues({
  cases,
  onDecide
}: {
  cases: SocialCase[];
  onDecide: (item: SocialCase, state: SocialCaseState, action: string, reason: string) => void;
}) {
  const [queue, setQueue] = useState<(typeof QUEUES)[number]>('All');
  const shown = queue === 'All' ? cases : cases.filter(item => item.queue === queue);
  return <div className="space-y-4" data-id="element-7011" data-mp="192pwspx0i15x">
      <Panel title="Campus feed moderation" data-id="element-7012" data-mp="193j53vbmcyq6">
        <ChoiceGroup label="Queue" options={QUEUES} value={queue} onChange={setQueue} data-id="element-7013" data-mp="37hu8rpj8663j" />
        <p className="mt-3 flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-7014" data-mp="37fhupngl8fvr">
          <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-7015" data-mp="33e4nb8xuk4mq" />
          Automated signals are inputs to a human decision, never the decision itself. Every action
          here is reversible on appeal, and the reason is shown to both the reporter and the author.
        </p>
      </Panel>

      {shown.length === 0 ? <Panel data-id="element-7016" data-mp="130e72laruyk0">
          <EmptyState icon={<FlagIcon className="h-5 w-5" data-id="element-7018" data-mp="12z358r0w00vj" />} title="This queue is clear" description="Nothing is waiting in this queue for the scope you are viewing." data-id="element-7017" data-mp="12ytcwwsddhmo" />
        </Panel> : <ul className="space-y-4" data-id="element-7019" data-mp="bat13tdgehs2">
          {shown.map(item => <li key={item.id} data-id="element-7020" data-mp="f9ihzvxpahgg">
              <CaseCard item={item} onDecide={onDecide} data-id="element-7021" data-mp="fcmy9rofx3nh" />
            </li>)}
        </ul>}
    </div>;
}
const ACTIONS = ['Removed', 'Restricted to campus', 'Renamed and marked student-run', 'Author warned', 'Account restricted from posting', 'No action'] as const;
function CaseCard({
  item,
  onDecide
}: {
  item: SocialCase;
  onDecide: (item: SocialCase, state: SocialCaseState, action: string, reason: string) => void;
}) {
  const [action, setAction] = useState<string>(ACTIONS[0]);
  const [reason, setReason] = useState('');
  const state = CASE_STATE[item.state];
  const settled = item.state === 'actioned' || item.state === 'dismissed';
  return <Panel title={item.subject} description={`${item.queue} · ${item.category} · reported by ${item.reportedBy}`} action={<div className="flex flex-wrap items-center gap-2" data-id="element-7023" data-mp="342m3jldv3l20">
          {item.priority === 'high' && <Badge tone="danger" data-id="element-7024" data-mp="3iqqh6ay256dx">High priority</Badge>}
          <Badge tone={state.tone} data-id="element-7025" data-mp="3isb9rv0taf95">{state.label}</Badge>
        </div>} data-id="element-7022" data-mp="jaqm334k1vp">
      <blockquote className="max-w-[76ch] border-l-4 border-line pl-4 text-body text-ink" data-id="element-7026" data-mp="3dzi5175cf6x2">
        {item.excerpt}
      </blockquote>

      <div className="mt-4 grid gap-4 lg:grid-cols-2" data-id="element-7027" data-mp="2q8mk8tzb1bzb">
        <div data-id="element-7028" data-mp="2lal0s7xyvanb">
          <h3 className="text-body font-semibold text-ink" data-id="element-7029" data-mp="2lb34fpmykr59">Signals</h3>
          <ul className="mt-2 space-y-1.5" data-id="element-7030" data-mp="10tbr4fdu9z18">
            {item.signals.map(signal => <li key={signal} className="text-body-sm text-ink" data-id="element-7031" data-mp="15ktutb78ydpw">
                {signal}
              </li>)}
          </ul>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-7032" data-mp="rugnnb26j31m">
            These are observations, not findings. No account is labelled fraudulent on the strength
            of them, and no internal risk score is shown here or to the account.
          </p>
        </div>
        <div data-id="element-7033" data-mp="1ojeh8py70ux7">
          <h3 className="text-body font-semibold text-ink" data-id="element-7034" data-mp="1oi3aqidd592e">Case</h3>
          <dl className="mt-2 space-y-1.5 text-body-sm" data-id="element-7035" data-mp="1ajn5kvv5fqmd">
            <div className="flex flex-wrap gap-x-2" data-id="element-7036" data-mp="1aj3rjq8mdmdm">
              <dt className="text-ink-muted" data-id="element-7037" data-mp="1fhorrpnvg622">Campus:</dt>
              <dd className="text-ink" data-id="element-7038" data-mp="1fkaxrkv46di6">{item.campusLabel}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2" data-id="element-7039" data-mp="3v8ay289dz3i1">
              <dt className="text-ink-muted" data-id="element-7040" data-mp="43uethkcpeqk">Assigned:</dt>
              <dd className="text-ink" data-id="element-7041" data-mp="41881k5ab39e">{item.assignedTo ?? 'Nobody yet'}</dd>
            </div>
            <div className="flex flex-wrap gap-x-2" data-id="element-7042" data-mp="jf13ft9b7yup">
              <dt className="text-ink-muted" data-id="element-7043" data-mp="elzj35qc373q">Case id:</dt>
              <dd className="cz-num text-ink" data-id="element-7044" data-mp="ekeojg40ro5v">{item.id.toUpperCase()}</dd>
            </div>
          </dl>
        </div>
      </div>

      {item.action && <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-7045" data-mp="5mumw628vpuq">
          <p className="text-body font-semibold text-ink" data-id="element-7046" data-mp="12cbyt2rtm84v">{item.action}</p>
          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-7047" data-mp="xcpn4xrn4zy6">{item.actionReason}</p>
        </div>}

      {item.appealNote && <div className="mt-3 rounded-md border-2 border-warn-200 bg-warn-50 p-4" data-id="element-7048" data-mp="ta2tqsfbn6yv">
          <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-7049" data-mp="ockjmypkilpa">
            <UndoIcon aria-hidden className="h-5 w-5 shrink-0 text-warn-700" data-id="element-7050" data-mp="3e1w7ta2zb3li" />
            Under appeal
          </p>
          <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-7051" data-mp="3918iqtyb8wtx">{item.appealNote}</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-7052" data-mp="1okr5gefltmz6">
            <Button variant="secondary" size="sm" onClick={() => onDecide(item, 'dismissed', 'Reinstated on appeal', 'Appeal upheld — the content is restored and the reporter is told why.')} data-id="element-7053" data-mp="1oi4qvuuaq77g">
              Uphold appeal and restore
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDecide(item, 'actioned', item.action ?? 'Removed', 'Appeal reviewed; original decision stands.')} data-id="element-7054" data-mp="2c9jxw0x0pcp1">
              Original decision stands
            </Button>
          </div>
        </div>}

      {!settled && !item.appealNote && <div className="mt-4 border-t border-line pt-4" data-id="element-7055" data-mp="qwh503vgv7a6">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-7056" data-mp="qwz91modepsn">
            <Field label="Action" htmlFor={`action-${item.id}`} data-id="element-7057" data-mp="1u74smm3pje3e">
              <Select id={`action-${item.id}`} value={action} onChange={event => setAction(event.target.value)} data-id="element-7058" data-mp="1u9h5wlyit0a4">
                {ACTIONS.map(option => <option key={option} value={option} data-id="element-7059" data-mp="1z6pouorht8cf">
                    {option}
                  </option>)}
              </Select>
            </Field>
            <Field label="Reason" htmlFor={`case-reason-${item.id}`} hint="Shown to the author and the reporter." data-id="element-7060" data-mp="2d45w22hm5658">
              <Input id={`case-reason-${item.id}`} value={reason} onChange={event => setReason(event.target.value)} data-id="element-7061" data-mp="2muqt3lhn22m1" />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-7062" data-mp="2vx01o5tcjlws">
            <Button variant="primary" size="sm" disabled={reason.trim().length === 0} onClick={() => onDecide(item, action === 'No action' ? 'dismissed' : 'actioned', action, reason.trim())} data-id="element-7063" data-mp="2qy5iv3vdpxco">
              Record decision
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onDecide(item, 'under_review', 'Taken for review', 'Assigned for a closer look.')} data-id="element-7064" data-mp="31ihdskmmeinv">
              Take for review
            </Button>
          </div>
          {reason.trim().length === 0 && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-7065" data-mp="2hv1lcrlmc1x0">
              A case cannot be closed without a reason.
            </p>}
        </div>}
    </Panel>;
}
const SETS = ['Community categories', 'Club categories', 'Challenge categories', 'Report categories'] as const;
function TaxonomyPanel() {
  const {
    activeScope
  } = useGovernance();
  const [set, setSet] = useState<(typeof SETS)[number]>('Community categories');
  const entries = socialTaxonomy.filter(entry => entry.set === set);
  return <PermissionGate permission="flags.edit" label="Editing social taxonomy" data-id="element-7066" data-mp="2od4oc7ps32vf">
      <div className="space-y-4" data-id="element-7067" data-mp="2jcgyvqh76u3b">
        <Panel title="Configuration, not code" data-id="element-7068" data-mp="2jbp60nezjagq">
          <ChoiceGroup label="Set" options={SETS} value={set} onChange={setSet} data-id="element-7069" data-mp="2fbeuywcc5tv0" />
          <p className="mt-3 max-w-[80ch] text-body-sm text-ink" data-id="element-7070" data-mp="2fdrcx9i3g482">
            Categories are configuration. Adding one takes no deployment. Where a tenant or campus
            has overridden the global list, the override is shown as such and the global value is
            kept beneath it — you always know where a value came from.
          </p>
        </Panel>

        {entries.length === 0 ? <Panel data-id="element-7071" data-mp="1vlkyw62l8skz">
            <EmptyState icon={<SlidersHorizontalIcon className="h-5 w-5" data-id="element-7073" data-mp="1rksr7h9d6vqt" />} title="Nothing configured in this set" description="Add the first value to make it available to this scope." data-id="element-7072" data-mp="1rlkn6t6vivhm" />
          </Panel> : <Panel bodyClassName="p-0" data-id="element-7074" data-mp="26t3f6wzsyly">
            <ul className="divide-y divide-line" data-id="element-7075" data-mp="73a1otxwotcr">
              {entries.map(entry => <li key={entry.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3.5 sm:px-6" data-id="element-7076" data-mp="b4vc88mywvjs">
                  <div className="min-w-0" data-id="element-7077" data-mp="3fjleu6ital29">
                    <p className="text-body font-medium text-ink" data-id="element-7078" data-mp="3fkmt0jv4jno7">{entry.value}</p>
                    <p className="cz-num text-body-sm text-ink-muted" data-id="element-7079" data-mp="3jkf2qzw97zv5">
                      In use by {entry.inUse} records
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-7080" data-mp="3tazxudcpym9b">
                    <Badge tone={entry.origin === 'Global' ? 'neutral' : 'brand'} data-id="element-7081" data-mp="3td2xlp3obtr8">
                      {entry.origin}
                    </Badge>
                    {entry.editableAt === 'Global' && activeScope.level !== 'global' ? <Badge plain icon={<LockIcon className="h-4 w-4" data-id="element-7083" data-mp="2alurdultqd2s" />} data-id="element-7082" data-mp="2ak9ye9f5r271">
                        Editable at global only
                      </Badge> : <Button variant="secondary" size="sm" data-id="element-7084" data-mp="21k3iyhzwfs58">
                        Edit
                      </Button>}
                  </div>
                </li>)}
            </ul>
          </Panel>}

        <Panel title="Removing a category" data-id="element-7085" data-mp="1rtieidxjntee">
          <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-7086" data-mp="1rsh50dw6feys">
            A category in use cannot simply be deleted — the records referencing it would lose their
            meaning. Retire it instead: it stops being offered on new records, and existing ones keep
            it until they are recategorised.
          </p>
        </Panel>
      </div>
    </PermissionGate>;
}